/**
 * Machine-readable candidate contract for `kiln-hand/1.0.0`.
 *
 * This deliberately contains semantic roles rather than renderer-specific CSS. It is a
 * POC companion to FOUNDATION_V1.md; production adoption belongs in @kiln/tokens.
 */

export const FOUNDATION_VERSION = 'kiln-foundation/1.0.0' as const;
export const HAND_VERSION = 'kiln-hand/1.0.0' as const;

export const kilnHandV1 = {
  color: {
    ground: '#FDFCF8',
    panel: '#ECEBE6',
    surface: '#FFFFFF',
    ink: '#1B1B1F',
    inkSoft: '#63676E',
    hairline: '#E7E2D6',
    inkEdge: '#0E0E12',
    brand: '#7C3AED',
    brandCta: '#6D28D9',
    brandTint: '#F0EAFD',
    brandEdge: '#5B21B6',
    blue: '#2E6BE6',
    blueTint: '#E9F0FE',
    amber: '#F09D1E',
    amberTint: '#FDF1DC',
  },
  type: {
    ui: 'Gabarito',
    mono: 'Spline Sans Mono',
    figureTitle: { size: 17, weight: 600, lineLimit: 2 },
    primaryLabel: { size: 14, weight: 600, lineLimit: 1 },
    annotation: { size: 12, minimumSize: 11, weight: 500, lineLimit: 2 },
    micro: { size: 11, weight: 600, lineLimit: 1 },
    value: { size: 12, minimumSize: 11, weight: 600, tabular: true },
  },
  stroke: {
    hairline: 1,
    structure: 1.5,
    causal: 2.5,
    activeIncrement: 0.75,
    lineCap: 'round',
    lineJoin: 'round',
  },
  radius: { cell: 14, chip: 8, panel: 20 },
  depth: { offsetY: 2, isometricAngleDegrees: 30, isometricDepthScale: 0.72 },
  layout: {
    baseSpace: 4,
    stageInset: 20,
    narrowStageInset: 16,
    minimumTarget: 44,
    canonicalWidths: [320, 390, 640],
    geometryQuantum: 1 / 64,
    maximumCallouts: 3,
    maximumUngroupedPeers: 7,
  },
  motion: {
    valueMs: 200,
    causalStepMs: 400,
    sceneMs: 260,
    revealMs: 500,
    revealHoldMs: 300,
    settleMs: 300,
    pressMs: 90,
    pressScale: 0.97,
    easeOut: [0.16, 1, 0.3, 1],
    spring: [0.34, 1.56, 0.64, 1],
    loopsAllowed: false,
    entranceMotionAllowed: false,
    causalMotionMayOverlap: false,
    reducedMotionUsesSameEndpoint: true,
  },
  teacherMark: {
    maximumJitterPx: 0.75,
    immutableGeometry: ['anchor', 'bounds', 'table-rule', 'causal-path', 'hit-region', 'text'],
  },
} as const;

export type SemanticColorRole =
  | 'structure'
  | 'annotation'
  | 'selection'
  | 'success'
  | 'address'
  | 'pointer'
  | 'traced-path'
  | 'value'
  | 'capacity'
  | 'cost'
  | 'warning'
  | 'unavailable';

export const semanticPaint: Record<
  SemanticColorRole,
  { readonly ink: 'ink' | 'inkSoft'; readonly stroke?: keyof typeof kilnHandV1.color; readonly fill?: keyof typeof kilnHandV1.color }
> = {
  structure: { ink: 'ink', stroke: 'ink', fill: 'surface' },
  annotation: { ink: 'inkSoft', stroke: 'inkSoft' },
  selection: { ink: 'ink', stroke: 'brand', fill: 'brandTint' },
  success: { ink: 'ink', stroke: 'brand', fill: 'brandTint' },
  address: { ink: 'ink', stroke: 'blue', fill: 'blueTint' },
  pointer: { ink: 'ink', stroke: 'blue' },
  'traced-path': { ink: 'ink', stroke: 'blue' },
  value: { ink: 'ink', stroke: 'amber', fill: 'amberTint' },
  capacity: { ink: 'ink', stroke: 'amber', fill: 'amberTint' },
  cost: { ink: 'ink', stroke: 'amber', fill: 'amberTint' },
  warning: { ink: 'ink', stroke: 'amber', fill: 'amberTint' },
  unavailable: { ink: 'inkSoft', stroke: 'inkSoft' },
};

export type FigureState = 'pending' | 'available' | 'active' | 'done' | 'wrong' | 'unavailable';
export type FoundationInteraction = 'select' | 'place' | 'connect' | 'order' | 'group' | 'scrub' | 'set';
export type FoundationView =
  | 'orthographic'
  | 'isometric'
  | 'section'
  | 'exploded'
  | 'schematic'
  | 'sequence'
  | 'plot'
  | 'table'
  | 'annotated-argument'
  | 'artifact';

export const primitiveKinds = [
  'cell',
  'grid',
  'surface',
  'slab',
  'container',
  'layerStack',
  'cutBoundary',
  'hatchRegion',
  'divider',
  'brace',
  'dimension',
  'connector',
  'causalArrow',
  'bus',
  'leader',
  'port',
  'label',
  'valueChip',
  'callout',
  'badge',
  'axis',
  'datumMark',
  'lane',
  'eventMark',
  'tableTrack',
  'artifactFrame',
  'hitRegion',
  'snapAnchor',
  'highlight',
  'ghost',
] as const;

export type PrimitiveKind = (typeof primitiveKinds)[number];

export const goldenFixtureKinds = [
  'minimum',
  'normal',
  'dense',
  'long-label',
  'phone-320',
  'phone-390',
  'wide-640',
  'pending',
  'active',
  'done',
  'wrong',
  'unavailable',
  'reduced-motion',
  'screen-reader',
] as const;

export type GoldenFixtureKind = (typeof goldenFixtureKinds)[number];

/**
 * Fields forbidden below the FigureSpec root. The host-supplied root viewport is the only
 * width/height exception; authored entities cannot carry geometry or visual tokens.
 */
export const forbiddenAuthoredPresentationFields = [
  'x',
  'y',
  'width',
  'height',
  'path',
  'd',
  'fill',
  'stroke',
  'color',
  'font',
  'fontSize',
  'strokeWidth',
  'radius',
  'easing',
  'duration',
] as const;
